import { BlogCardGrid } from '../components/BlogCard';
import { CtaBanner } from '../components/CtaBanner';
import { FAQAccordion } from '../components/FAQAccordion';
import { InsuranceCardGrid } from '../components/InsuranceCard';
import { ButtonLink } from '../components/ui/Button';
import { Container } from '../components/ui/Container';
import { Icon } from '../components/ui/Icon';
import { Section, SectionHeading } from '../components/ui/Section';
import { useShared } from '../components/layout/Layout';
import type { HomeProps } from '../types';
import { splitBrandWordmark } from '../utils/brand';
import { t } from '../i18n/t';

/** Bare accent icons cycling the brand palette; tints read unevenly on the muted band. */
const ADVANTAGE_ACCENTS = ['text-secondary-500', 'text-accent-500', 'text-warning-600'];

const STEP_ACCENTS = ['text-secondary-500/70', 'text-accent-500/70', 'text-warning-500/80'];

export default function Home({ hero, advantages, steps, faq, posts }: HomeProps) {
  const { products } = useShared();

  return (
    <>
      <Hero hero={hero} />

      <Section id="apdrosinasanas-veidi" aria-labelledby="products-heading" className="!py-20 sm:!py-24 lg:!py-32">
        {/* Editorial split: the heading holds the left rail while the grid scrolls past it */}
        <div className="grid gap-12 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] lg:gap-16">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <SectionHeading
              id="products-heading"
              align="left"
              eyebrow={t('home.sections.products.eyebrow')}
              title={t('home.sections.products.title')}
              subtitle={t('home.sections.products.subtitle')}
            />
          </div>
          <InsuranceCardGrid products={products} columns={2} />
        </div>
      </Section>

      <Section tone="muted" aria-labelledby="advantages-heading" className="!py-20 sm:!py-24 lg:!py-28">
        <SectionHeading
          id="advantages-heading"
          align="left"
          eyebrow={t('home.sections.advantages.eyebrow')}
          title={t('home.sections.advantages.title')}
        />
        <ul className="mt-16 grid gap-x-16 sm:grid-cols-2">
          {advantages.map((advantage, index) => (
            <li key={advantage.title} className="border-t border-slate-300/70 py-9">
              <span
                className={`inline-flex ${ADVANTAGE_ACCENTS[index % ADVANTAGE_ACCENTS.length]}`}
                aria-hidden="true"
              >
                <Icon name={advantage.icon} className="h-8 w-8" strokeWidth={1.5} />
              </span>
              <h3 className="mt-6 font-display text-lg font-extrabold uppercase leading-tight tracking-[-0.01em] text-primary-500">
                {advantage.title}
              </h3>
              <p className="mt-3 text-base font-light leading-[1.65] tracking-normal text-slate-600">
                {advantage.description}
              </p>
            </li>
          ))}
        </ul>
      </Section>

      <Section aria-labelledby="steps-heading" className="!py-20 sm:!py-24 lg:!py-28">
        <SectionHeading
          id="steps-heading"
          align="left"
          eyebrow={t('home.sections.steps.eyebrow')}
          title={t('home.sections.steps.title')}
        />
        <ol className="mt-16 grid gap-10 sm:grid-cols-3 lg:gap-14">
          {steps.map((step, index) => (
            <li key={step.title} className="border-t-2 border-primary-500 pt-7">
              <span
                className={`font-display text-[4rem] font-black leading-none tracking-[-0.05em] sm:text-[4.5rem] ${
                  STEP_ACCENTS[index % STEP_ACCENTS.length]
                }`}
                aria-hidden="true"
              >
                {String(index + 1).padStart(2, '0')}
              </span>
              <h3 className="mt-6 font-display text-lg font-extrabold uppercase leading-tight tracking-[-0.01em] text-primary-500">
                <span className="sr-only">{t('ui.home.step', { n: index + 1 })} </span>
                {step.title}
              </h3>
              <p className="mt-3 text-base font-light leading-[1.65] tracking-normal text-slate-600">
                {step.description}
              </p>
            </li>
          ))}
        </ol>
        <div className="mt-14">
          <ButtonLink to="/pieteikums" variant="accent" size="lg">
            {t('ui.home.apply')}
            <Icon name="arrow-right" className="h-5 w-5" strokeWidth={2} />
          </ButtonLink>
        </div>
      </Section>

      {/* Testimonials kept for data compatibility; hidden pending content readiness.
      <Section tone="muted" aria-labelledby="testimonials-heading">
        ...
      </Section>
      */}

      <Section size="narrow" aria-labelledby="faq-heading">
        <SectionHeading
          id="faq-heading"
          align="left"
          eyebrow={t('home.sections.faq.eyebrow')}
          title={t('home.sections.faq.title')}
        />
        <div className="mt-12">
          <FAQAccordion entries={faq} idPrefix="home-faq" />
        </div>
      </Section>

      {posts.length > 0 && (
        <Section tone="muted" aria-labelledby="posts-heading">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading
              id="posts-heading"
              align="left"
              eyebrow={t('home.sections.blog.eyebrow')}
              title={t('home.sections.blog.title')}
            />
            <ButtonLink to="/blogs" variant="secondary">
              {t('ui.home.all_posts')}
            </ButtonLink>
          </div>
          <div className="mt-14 sm:mt-16">
            <BlogCardGrid posts={posts} />
          </div>
        </Section>
      )}

      <CtaBanner />
    </>
  );
}

function Hero({ hero }: { hero: HomeProps['hero'] }) {
  const { brand } = useShared();
  const { brandName, tld } = splitBrandWordmark(brand.name);

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-primary-500 to-secondary-500">
      {/* Teal glow keeps the navy-to-blue ramp from reading flat */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(85%_65%_at_100%_105%,rgba(26,188,156,0.32),transparent_55%)]"
        aria-hidden="true"
      />

      <Container>
        <div className="relative py-8 sm:py-28 lg:py-36">
          <p className="animate-fade-up flex items-center gap-3 text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-accent-300">
            <span aria-hidden="true" className="h-px w-8 bg-current opacity-60" />
            <span>
              {brandName}
              {tld && <span className="text-white/70">{tld}</span>}
            </span>
          </p>

          {/* Leading stays above 1 so Latvian diacritics clear the line above */}
          <h1 className="animate-fade-up-delay-2 mt-8 max-w-4xl font-display text-[2.25rem] font-black uppercase leading-[1.06] tracking-[-0.03em] text-white sm:mt-10 sm:text-[3.5rem] lg:text-[4.25rem]">
            {hero.title}
          </h1>
          <p className="animate-fade-up-delay-3 mt-7 max-w-xl text-lg font-light leading-[1.6] tracking-normal text-white/80">
            {hero.subtitle}
          </p>

          {hero.highlights.length > 0 && (
            <ul className="animate-fade-up-delay-3 mt-9 flex flex-wrap gap-x-7 gap-y-3">
              {hero.highlights.map((highlight) => (
                <li
                  key={highlight}
                  className="flex items-center gap-2.5 text-sm font-medium tracking-[-0.005em] text-white/90"
                >
                  <Icon name="check" className="h-4 w-4 shrink-0 text-accent-300" strokeWidth={2.5} />
                  {highlight}
                </li>
              ))}
            </ul>
          )}

          <div className="animate-fade-up-delay-4 mt-11 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonLink to="/pieteikums" variant="accent" size="lg">
              {t('ui.home.apply')}
              <Icon name="arrow-right" className="h-5 w-5" strokeWidth={2} />
            </ButtonLink>
            <ButtonLink to="/privatpersonam" variant="secondary" size="lg">
              {t('ui.home.learn_more')}
            </ButtonLink>
          </div>

          {/* {hero.stats.length > 0 && (
            <dl className="animate-fade-up-delay-4 mt-16 grid max-w-3xl grid-cols-1 gap-px overflow-hidden border border-white/15 bg-white/15 sm:mt-20 sm:grid-cols-3">
              {hero.stats.map((stat) => (
                <div key={stat.label} className="bg-primary-500/25 px-6 py-7 backdrop-blur-sm">
                  <dt className="font-display text-[2.25rem] font-black leading-none tracking-[-0.04em] text-white sm:text-[2.75rem]">
                    {stat.value}
                  </dt>
                  <dd className="mt-3 text-[0.8125rem] font-medium uppercase leading-snug tracking-[0.1em] text-white/70">
                    {stat.label}
                  </dd>
                </div>
              ))}
            </dl>
          )} */}
        </div>
      </Container>
    </section>
  );
}
